import { createSlice } from '@reduxjs/toolkit';
import type {
  DerivedUnknownEvents,
  DriverBehaviorData,
  LocationSegment,
  LocationSegmentStats,
  TripSummary,
} from 'types/driver.behavior.types';

export interface driverBehaviorState {
  data: DriverBehaviorData;
  chartData7D: DriverBehaviorData;
  isLoading: boolean;
  isLoadingGearMatrix: boolean;
  error: {
    timelineError: string | null;
  };
  polygonPointsStats: LocationSegmentStats[];
  polygonPoints: LocationSegment[];
  /**
   * "Unknown" spans the engine-hours timeline derives on the frontend (gaps in an
   * asset's known states). Kept separate from `data` so writing it back can't
   * re-trigger the memo chain that produced it.
   */
  derivedUnknownEvents: DerivedUnknownEvents;
  /**
   * Per-device trip counts from the driver-behavior response. Kept beside `data`
   * rather than inside it because `DriverBehaviorData` values are all event arrays.
   */
  tripSummary: TripSummary | null;
}

const initialState: driverBehaviorState = {
  data: {
    braking: [],
    overspeed: [],
    underspeed: [],
    incorrect_gear: [],
    productive: [],
    short_idling: [],
    excess_idling: [],
    engine_hours: [],
    acceleration: [],
    gear_skip: [],
    gear_count_matrix: [],
    moving_loaded: [],
    moving_unloaded: [],
    idling_stop: [],
    mucking: [],
    loading_spotting: [],
    unloading_spotting: [],
    queuing: [],
    allowed_break: [],
    unknown: [],
  },
  chartData7D: {
    braking: [],
    overspeed: [],
    underspeed: [],
    incorrect_gear: [],
    productive: [],
    short_idling: [],
    excess_idling: [],
    engine_hours: [],
    acceleration: [],
    gear_skip: [],
    gear_count_matrix: [],
    moving_loaded: [],
    moving_unloaded: [],
    idling_stop: [],
    mucking: [],
    loading_spotting: [],
    unloading_spotting: [],
    queuing: [],
    allowed_break: [],
    unknown: [],
  },
  isLoading: true,
  isLoadingGearMatrix: false,
  error: {
    timelineError: null,
  },
  polygonPointsStats: [],
  polygonPoints: [],
  derivedUnknownEvents: {
    rangeKey: '',
    segments: [],
  },
  tripSummary: null,
};

export const driverBehaviorSlice = createSlice({
  name: 'driverBehavior',
  initialState,
  reducers: {
    getAll(state, { payload }) {
      return {
        ...state,
        data: {
          ...state.data,
          ...payload,
        },
        isLoading: false,
      };
    },
    setIsLoading(state, { payload }) {
      state.isLoading = payload;
    },
    setIsLoadingGearMatrix(state, { payload }) {
      state.isLoadingGearMatrix = payload;
    },
    setTimelineError(state, { payload }: { payload: string }) {
      state.error.timelineError = payload;
    },
    resetTimelineError(state) {
      state.error.timelineError = null;
    },
    setGearCountMatrix(state, { payload }: { payload: any[] }) {
      state.data.gear_count_matrix = payload;
    },
    setPolygonPoints(state, { payload }: { payload: any }) {
      state.polygonPoints = payload.polygonPoints;
      state.polygonPointsStats = payload.polygonPointsStats;
    },
    setChartData7D(state, { payload }: { payload: DriverBehaviorData }) {
      state.chartData7D = payload;
    },
    setDerivedUnknownEvents(
      state,
      { payload }: { payload: DerivedUnknownEvents }
    ) {
      state.derivedUnknownEvents = payload;
    },
    resetDerivedUnknownEvents(state) {
      state.derivedUnknownEvents = { rangeKey: '', segments: [] };
    },
    setTripSummary(state, { payload }: { payload: TripSummary | null }) {
      state.tripSummary = payload;
    },
  },
});

export const {
  getAll,
  setIsLoading,
  setIsLoadingGearMatrix,
  setTimelineError,
  resetTimelineError,
  setGearCountMatrix,
  setPolygonPoints,
  setChartData7D,
  setDerivedUnknownEvents,
  resetDerivedUnknownEvents,
  setTripSummary,
} = driverBehaviorSlice.actions;
export default driverBehaviorSlice.reducer;
